import { getEquipmentPortCapabilities } from '../data/equipmentPorts';
import { PortCapability } from '../types/portCapabilities';
import type { SimpleLayout } from '../types';
import { makeTemplateForPorts } from './layoutTemplates';

export interface EquipmentPreviewFeature {
  key: string;
  label: string;
  color: string;
}

export interface EquipmentPreviewSummary {
  layout: SimpleLayout | null;
  faceLabel?: string;
  features: EquipmentPreviewFeature[];
}

const KIND_META: Record<string, { label: string; color: string }> = {
  rj45: { label: 'RJ45', color: '#34d399' },
  sfp: { label: 'SFP', color: '#60a5fa' },
  sfpPlus: { label: 'SFP+', color: '#3b82f6' },
  'sfp+': { label: 'SFP+', color: '#3b82f6' },
  ac: { label: 'AC', color: '#ef4444' },
  dc: { label: 'DC', color: '#f97316' },
  usb: { label: 'USB', color: '#8b5cf6' },
  console: { label: 'Console', color: '#06b6d4' },
  hdmi: { label: 'HDMI', color: '#ec4899' },
  audio: { label: 'Audio', color: '#14b8a6' },
  coax: { label: 'Coax', color: '#a855f7' },
  custom: { label: 'Custom', color: '#94a3b8' },
};

function poeShortName(standard?: string): string {
  if (standard === '802.3bt') return 'bt';
  if (standard === '802.3at') return 'at';
  if (standard === '802.3af') return 'af';
  return 'af/at';
}

function inferWifi(equipment: any): string {
  const speed = String(equipment?.specifications?.speed || '');
  if (/wi-?fi/i.test(speed)) return speed;
  if (equipment?.type === 'ap') return speed || 'Yes';
  return 'None';
}

function inferPoeFromSpecs(equipment: any): string | null {
  const blob = `${equipment?.specifications?.power || ''} ${equipment?.specifications?.features || ''}`;
  if (/802\.3bt|poe\+\+/i.test(blob)) return 'bt';
  if (/802\.3at|poe\+/i.test(blob)) return 'at';
  if (/802\.3af|\bpoe\b/i.test(blob)) return 'af/at';
  return null;
}

function isFiberKind(kind: string, cap?: PortCapability): boolean {
  const physical = cap?.physical?.type;
  return (
    kind === 'sfp' ||
    kind === 'sfpPlus' ||
    kind === 'sfp+' ||
    physical === 'sfp' ||
    physical === 'sfp+' ||
    physical === 'fiber' ||
    !!cap?.speed?.sfp ||
    !!cap?.speed?.sfpPlus
  );
}

export function getCatalogPreviewLayout(equipment: any, savedConfig?: any): { layout: SimpleLayout | null; faceLabel?: string } {
  const layout = savedConfig?.layout;
  if (layout?.multiFace?.faces) {
    const faces = layout.multiFace.faces;
    const preferred = layout.multiFace.defaultFace || 'front';
    const faceKey = faces[preferred] ? preferred : (faces.front ? 'front' : Object.keys(faces).find((key) => faces[key]?.ports?.length));
    if (faceKey && faces[faceKey]) {
      return {
        layout: faces[faceKey] as SimpleLayout,
        faceLabel: faceKey.charAt(0).toUpperCase() + faceKey.slice(1),
      };
    }
  }
  if (layout?.simple) {
    return { layout: layout.simple as SimpleLayout };
  }
  const portCount =
    Number(equipment?.specifications?.ports) ||
    (getEquipmentPortCapabilities(equipment?.id)?.length || 0);
  if (portCount > 0) {
    return { layout: makeTemplateForPorts(portCount) };
  }
  return { layout: null };
}

export function summarizeEquipmentPreview(equipment: any, savedConfig?: any): EquipmentPreviewSummary {
  const { layout, faceLabel } = getCatalogPreviewLayout(equipment, savedConfig);
  const capabilities: PortCapability[] = getEquipmentPortCapabilities(equipment?.id) || [];
  const ports = layout?.ports || [];
  const buckets = new Map<string, { count: number; label: string; color: string }>();

  const add = (key: string, label: string, color: string, count = 1) => {
    const existing = buckets.get(key);
    if (existing) existing.count += count;
    else buckets.set(key, { count, label, color });
  };

  if (ports.length > 0) {
    ports.forEach((port, index) => {
      const cap = capabilities[(port.number || 0) - 1] || capabilities[index];
      const kind = String(port.kind || cap?.physical?.type || 'rj45');

      if (isFiberKind(kind, cap)) {
        const plus = kind === 'sfpPlus' || kind === 'sfp+' || cap?.physical?.type === 'sfp+' || cap?.speed?.sfpPlus;
        if (plus) add('sfpPlus', 'SFP+', KIND_META.sfpPlus.color);
        else add('sfp', 'SFP', KIND_META.sfp.color);
        return;
      }

      const meta = KIND_META[kind];
      if (kind === 'rj45' || !meta) {
        if (cap?.power?.poe) {
          const std = poeShortName(cap.power.poeStandard);
          add(`poe:${std}`, `PoE ${std}`, '#10b981');
        } else {
          add('rj45', 'RJ45', KIND_META.rj45.color);
        }
        return;
      }

      add(kind, meta.label, meta.color);
    });
  } else if (capabilities.length > 0) {
    capabilities.forEach((cap) => {
      const kind = String(cap.physical?.type || 'rj45');
      if (isFiberKind(kind, cap)) {
        const plus = kind === 'sfp+' || cap.speed?.sfpPlus;
        if (plus) add('sfpPlus', 'SFP+', KIND_META.sfpPlus.color);
        else add('sfp', 'SFP', KIND_META.sfp.color);
        return;
      }
      if (cap.power?.poe) {
        const std = poeShortName(cap.power.poeStandard);
        add(`poe:${std}`, `PoE ${std}`, '#10b981');
      } else {
        add('rj45', 'RJ45', KIND_META.rj45.color);
      }
    });
  } else {
    const portCount = Number(equipment?.specifications?.ports) || 0;
    if (portCount > 0) {
      const poe = inferPoeFromSpecs(equipment);
      if (poe) add('poe', `PoE ${poe}`, '#10b981', portCount);
      else add('rj45', 'RJ45', KIND_META.rj45.color, portCount);
    }
  }

  const poeAf = buckets.get('poe:af');
  const poeAt = buckets.get('poe:at');
  if (poeAf && poeAt) {
    buckets.delete('poe:af');
    buckets.delete('poe:at');
    add('poe:af/at', 'PoE af/at', '#10b981', poeAf.count + poeAt.count);
  }

  const features: EquipmentPreviewFeature[] = Array.from(buckets.entries()).map(([key, value]) => ({
    key,
    label: `${value.count}x ${value.label}`,
    color: value.color,
  }));

  const wifi = inferWifi(equipment);
  features.push({
    key: 'wifi',
    label: `Wifi: ${wifi}`,
    color: wifi === 'None' ? '#94a3b8' : '#eab308',
  });

  return { layout, faceLabel, features };
}
